import { api, send, on, off } from '../serverClient';
import { CURRENCY } from './profileDefaults';

// Entry cost: 3 packs at 20% discount (must match server's computeEntryCost)
export const DRAFT_ENTRY_COST = Math.floor(CURRENCY.PACK_PRICE * 3 * 0.8);

// Sealed entry cost: 6 packs at the same 20% limited-mode discount
// (must match server's computeSealedEntryCost). 240 coins at PACK_PRICE 50.
export const SEALED_ENTRY_COST = Math.floor(CURRENCY.PACK_PRICE * 6 * 0.8);

// Keystone alternative. Sealed costs two keystones because the player
// opens twice as many packs as a draft entry buys. Must match server's
// keystoneCost(mode) in models/drafts.js.
export const DRAFT_KEYSTONE_COST = 1;
export const SEALED_KEYSTONE_COST = 2;
export function keystoneCostForMode(modeKey) {
  return modeKey === 'sealed' ? SEALED_KEYSTONE_COST : DRAFT_KEYSTONE_COST;
}
export function entryCostForMode(modeKey) {
  return modeKey === 'sealed' ? SEALED_ENTRY_COST : DRAFT_ENTRY_COST;
}

// Draft event visibility options
export const DRAFT_VISIBILITY = {
  PUBLIC: 'public',
  PRIVATE: 'private',
};

// Draft event status values (matches server enum)
export const DRAFT_STATUS = {
  OPEN: 'open',
  DRAFTING: 'drafting',
  BUILDING: 'building',
  TOURNAMENT: 'tournament',
  COMPLETE: 'complete',
  CANCELLED: 'cancelled',
};

// --- REST endpoints ---

export async function listDraftEvents(filters = {}) {
  const params = new URLSearchParams();
  if (filters.visibility) params.set('visibility', filters.visibility);
  if (filters.status) params.set('status', filters.status);
  if (filters.setKey) params.set('setKey', filters.setKey);
  const qs = params.toString();
  return api.get(`/draft/events${qs ? `?${qs}` : ''}`);
}

export async function getDraftEvent(eventId) {
  return api.get(`/draft/events/${encodeURIComponent(eventId)}`);
}

// The player's in-progress tournaments (drafting / building / tournament),
// for the "Your Tournaments" tab so they can always return to one.
export async function getMyTournaments() {
  const res = await api.get('/draft/events/mine');
  return res?.events || [];
}

// `paymentMethod` is 'coins' (default) or 'keystone'. When omitted,
// the server falls back to 'coins'.
export async function createDraftEvent({ setKey, podSize, scheduledAt, visibility, mode, paymentMethod }) {
  return api.post('/draft/events', { setKey, podSize, scheduledAt, visibility, mode, paymentMethod });
}

export async function joinDraftEvent(eventId, { paymentMethod } = {}) {
  return api.post(`/draft/events/${encodeURIComponent(eventId)}/join`, { paymentMethod });
}

// Forwards the post-refund keystone / coin balance returned by the
// server to any listener (app.jsx) so the header counter updates
// without waiting for the profile:update WS push to land. The WS
// push is still the durable source of truth. This event just
// closes the race window where the user navigates away the moment
// cancel/leave succeeds and would otherwise miss the visual tick.
function broadcastRefundedBalance(result) {
  if (!result) return result;
  const hasBalance = result.keystones != null || result.coins != null;
  if (!hasBalance) return result;
  try {
    window.dispatchEvent(new CustomEvent('valkenhall:draft-refund', {
      detail: { keystones: result.keystones, coins: result.coins },
    }));
  } catch {}
  return result;
}

export async function leaveDraftEvent(eventId) {
  const result = await api.post(`/draft/events/${encodeURIComponent(eventId)}/leave`);
  return broadcastRefundedBalance(result);
}

export async function cancelDraftEvent(eventId) {
  const result = await api.post(`/draft/events/${encodeURIComponent(eventId)}/cancel`);
  return broadcastRefundedBalance(result);
}

export async function startDraftEarly(eventId) {
  return api.post(`/draft/events/${encodeURIComponent(eventId)}/start`);
}

export async function skipDraft(eventId) {
  return api.post(`/draft/events/${encodeURIComponent(eventId)}/skip-draft`);
}

export async function inviteToDraft(eventId, playerIds) {
  return api.post(`/draft/events/${encodeURIComponent(eventId)}/invite`, { playerIds });
}

// Invitee-side decline. Server removes the player's draft_invites row,
// pings the creator via WS, and auto-cancels the event (refunding the
// creator's keystone / coins via refundAll) when no other participants
// or pending invites remain for a private draft. Fire-and-forget from
// the client. Failures are non-fatal because the local chat / mail
// state has already been marked declined.
export async function declineDraftEvent(eventId) {
  return api.post(`/draft/events/${encodeURIComponent(eventId)}/decline`);
}

// `cards` is a flat array of `{ cardId, printingId, cardName?, isSideboard? }`
// entries. Same shape as constructed-deck saves. The avatar is just another
// entry; the server and game board identify it by card.type at read time.
export async function submitDraftDeck(eventId, { cards }) {
  return api.post(`/draft/events/${encodeURIComponent(eventId)}/deck`, { cards });
}

// Autosave / restore the in-progress deck-build state (zone assignments +
// avatar) so progress survives leaving and rejoining.
export async function saveDraftWorkingDeck(eventId, workingDeck) {
  return api.put(`/draft/events/${encodeURIComponent(eventId)}/deck-draft`, { workingDeck });
}

export async function getDraftWorkingDeck(eventId) {
  const res = await api.get(`/draft/events/${encodeURIComponent(eventId)}/deck-draft`);
  return res?.workingDeck || null;
}

export async function getDraftStandings(eventId) {
  return api.get(`/draft/events/${encodeURIComponent(eventId)}/standings`);
}

export async function getDraftPickHistory(eventId) {
  return api.get(`/draft/events/${encodeURIComponent(eventId)}/picks`);
}

export async function reportDraftMatchResult(eventId, matchId, winnerId) {
  return api.post(`/draft/events/${encodeURIComponent(eventId)}/match-result`, { matchId, winnerId });
}

export async function requestDraftMatchStart(eventId, matchId) {
  return api.post(`/draft/events/${encodeURIComponent(eventId)}/match/${encodeURIComponent(matchId)}/request-start`, {});
}

export async function confirmDraftMatchReady(eventId, matchId) {
  return api.post(`/draft/events/${encodeURIComponent(eventId)}/match/${encodeURIComponent(matchId)}/ready`, {});
}

export async function unreadyDraftMatch(eventId, matchId) {
  return api.post(`/draft/events/${encodeURIComponent(eventId)}/match/${encodeURIComponent(matchId)}/unready`, {});
}

// Called when the tournament dashboard opens. If the opponent is already
// waiting at our current match, the server pushes us the ready-check
// (presence auto-prompt) so we don't have to click Play Match to find out.
export async function syncDraftReadyPresence(eventId) {
  return api.post(`/draft/events/${encodeURIComponent(eventId)}/sync-ready`, {});
}

// Tell the other participants that we left a started event (building or
// tournament) so they're informed instead of silently building/waiting alone.
// Best-effort; never blocks the client's own navigation away.
export async function leaveDraftTournament(eventId) {
  return api.post(`/draft/events/${encodeURIComponent(eventId)}/leave-tournament`, {});
}

// Pending "your opponent is ready, join your match" invites for the caller,
// shown in the Notifications menu. Targeted to the exact round opponent.
export async function getDraftMatchInvites() {
  const res = await api.get('/draft/match-invites');
  return res?.invites || [];
}

// "Not now" on a round summons. Server-side, so it holds until the
// opponent asks again instead of resurfacing on the next refetch.
export async function dismissDraftMatchInvite(matchId) {
  return api.post(`/draft/match-invites/${encodeURIComponent(matchId)}/dismiss`, {});
}

// Draft/sealed lobbies the caller was invited to and hasn't answered.
// Server-derived (draft_invites x open events), replacing the old scan
// of chat messages that could not notice a cancelled event.
export async function getDraftInvites() {
  const res = await api.get('/draft/invites');
  return res?.invites || [];
}

// "I'm willing, my opponent isn't." Claim a forfeit win when you've
// readied and your opponent never showed within the grace window. The
// server validates (canClaimForfeit) and 400s with { error, retryInMs }
// if it's too soon or the opponent is present.
export async function claimDraftMatchForfeit(eventId, matchId) {
  return api.post(`/draft/events/${encodeURIComponent(eventId)}/match/${encodeURIComponent(matchId)}/claim-forfeit`, {});
}

// --- WebSocket helpers ---

export function sendDraftPick(cardId, printingId) {
  return send('draft:pick', { cardId, printingId });
}

// Ask the server to re-send our current pack. Used by DraftPicker when it
// resumes (mounts after the original pack broadcast was already delivered and
// dropped) and is still waiting for cards.
export function requestDraftPack() {
  return send('draft:request_pack', {});
}

// Post-match acknowledgement for a draft tournament round. Fired when
// the player dismisses the rewards modal (ArenaMatchResult Continue
// button). The server gates round N+1 pairings on both participants in
// every round-N match having sent this. Otherwise the loser's reward
// claim races against the freshly-created next-round room and zeros
// out, and the next opponent can drag this player into the new match
// before they've finished reading the result screen.
export function sendDraftMatchAck(matchId) {
  return send('draft:match_ack', { matchId });
}

export function onDraftEvent(eventType, callback) {
  return on(`draft:${eventType}`, callback);
}

export function offDraftEvent(eventType, callback) {
  off(`draft:${eventType}`, callback);
}

// Subscribe to all draft lifecycle events. Returns an unsubscribe function.
export function subscribeToDraftEvents(handlers) {
  const unsubs = [];
  const events = [
    'started', 'pack', 'pick_confirmed', 'pack_complete',
    'timer', 'auto_picked', 'building', 'deck_submitted',
    'round_start', 'round_result', 'complete',
    'joined', 'left', 'cancelled', 'invite',
    // Disconnect lifecycle. Fired by ws/draft.js when a seat's
    // socket closes (player_disconnected) or returns within the
    // grace window (player_reconnected). Omitting them from this
    // allow-list silently drops global subscribers, which is
    // exactly how the sealed "opponent vanished" bug survived.
    // app.jsx's toast + cancel-modal handlers never fired.
    'player_disconnected', 'player_reconnected', 'player_left',
    // Per-round-match ready-check lifecycle. match_ready_check
    // opens the "Are you ready?" modal on both seats; match_start
    // carries the authoritative roomId for the GameBoard mount.
    'match_ready_check', 'match_participant_ready', 'match_start', 'match_ready_left',
    // Picks-so-far snapshot, sent on resume so the picker can rebuild the
    // player's drafted-pool sidebar after an app reload.
    'my_picks',
  ];
  for (const event of events) {
    if (handlers[event]) {
      unsubs.push(on(`draft:${event}`, handlers[event]));
    }
  }
  return () => unsubs.forEach((unsub) => unsub());
}
